import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { get as _get, isEmpty as _isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import Preview from './Preview/Preview';
import VersionHistory from './VersionHistory/VersionHistory';
import TopNav from './TopNav/TopNav';
import LeftPanel from './LeftPanel/LeftPanel';
import Canvas from './Canvas/Canvas';
import RightPanel from './RightPanel/RightPanel';
import Notifier from '../componentLibrary/Notifier';
import LoadingMessage from '../componentLibrary/LoadingMessage';
import CustomFonts from '../componentLibrary/FontSelect/CustomFonts';
import Toolbar from './CenterPanel/Toolbar';
import ForceUpdateFont from './Modal/ForceUpdateFont';
import * as browserActions from '../actions/browser';
import * as modalActions from '../actions/modals';
import * as Styled from './styles';
import { blockType } from '../types';
import { APPLICATION_MODES, DRAFT_ACCESS } from '../constants/itemTypes/itemTypes';
import elementTypes from '../constants/elementTypes';
import { getSingleUseTagsBegin } from '../actions/personalization';
import eventService from '../common/eventService';

const DESKTOP_VIEW_WIDTH = '100%';

class Editor extends Component {
    state = {
        shouldLeftPanelBeCollapsed: false,
        leftPanelCollapsedByUser: false,
        previewWidth: DESKTOP_VIEW_WIDTH,
        missingFonts: [],
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { browserWidth, browserHeight } = nextProps;
        return {
            ...prevState,
            shouldLeftPanelBeCollapsed: browserWidth < 1296 || browserHeight < 475,
        };
    }

    componentDidMount() {
        const { isCustomFontsLoaded, dispatch, flags, isGetSingleUseTagsLoading } = this.props;
        if (isCustomFontsLoaded) {
            this.checkForMissingFonts();
        }

        if (flags.ed563MailMergeV2 === true && !isGetSingleUseTagsLoading) {
            dispatch(getSingleUseTagsBegin());
            eventService.getSingleUseTagData();
        }
    }

    componentDidUpdate(prevProps) {
        const { customFonts } = this.props;
        if (prevProps.customFonts?.length !== customFonts.length) {
            this.checkForMissingFonts();
        }
    }

    // TODO: This check will be removed once the tech debt about UNDO remove selection is fixed.
    checkIfElementExists = (data) => {
        const { canvasBlocks, selectedElements } = this.props;
        const elmIdx = Object.keys(selectedElements)[0];
        const { blockIndex, columnIdx } = data;
        const blockContainingElement = canvasBlocks.find((block) => block.index === blockIndex);

        if (!blockContainingElement) {
            return false;
        }
        const columnContainingElement =
            Object.keys(blockContainingElement).length > 0 &&
            blockContainingElement.content.columns.find((column) => column.columnIdx === columnIdx);

        if (!columnContainingElement) {
            return false;
        }

        const elementExists =
            Object.keys(columnContainingElement).length > 0 &&
            columnContainingElement.columnItems.find((elm) => elm.elmIdx === elmIdx);

        return elementExists;
    };

    checkForMissingFonts = () => {
        const { customFonts, canvasBlocks, hasCustomFontsAccess, flags, dispatch } = this.props;
        const { pik1913TextEditorModule } = flags;
        const isCanvasEmpty = canvasBlocks.length === 0;

        if (!pik1913TextEditorModule && hasCustomFontsAccess && !isCanvasEmpty) {
            const canvasFonts = canvasBlocks.reduce((acc, val) => {
                val?.content?.columns?.forEach((col) => {
                    col?.columnItems?.forEach((colItem) => {
                        if (colItem.type === 'PARAGRAPH') {
                            const { fontFamily, fontID } = colItem.content;
                            if (fontFamily && fontID) {
                                acc.add(fontFamily);
                            }
                        }
                    });
                });
                return acc;
            }, new Set());

            const missingFonts = Array.from(canvasFonts).filter((font) => !customFonts.some((f) => f.value === font));

            if (!_isEmpty(missingFonts)) {
                this.setState({ missingFonts }, () => {
                    dispatch(modalActions.setModal({ name: 'forceUpdateFont', status: true }));
                });
            }
        }
    };

    render() {
        const {
            canvasBlocks,
            selectedElements,
            isConnected,
            integration,
            hasRTEAccess,
            isForceUpdateFontModalOpen,
            hasCustomFontsAccess,
            isCustomFontsLoaded,
            flags,
            appMode,
            dispatch,
        } = this.props;
        const isCanvasEmpty = canvasBlocks.length === 0;
        const { shouldLeftPanelBeCollapsed, leftPanelCollapsedByUser, previewWidth, missingFonts } = this.state;
        const leftPanelCollapsed = shouldLeftPanelBeCollapsed || leftPanelCollapsedByUser;
        const leftPanelWidth = leftPanelCollapsed ? 64 : 250;
        const canvasContainer = document.getElementById('canvasContainer');
        let shouldDisplayToolbar = false;
        const displayErrorAddingBlock = canvasBlocks.length >= 40 && integration === 'PUBLIC_PREVIEW';
        const displayInternetWarning = hasRTEAccess && !isConnected;

        if (canvasContainer && isCanvasEmpty) {
            canvasContainer.scrollTop = 0;
        }

        if (Object.keys(selectedElements).length === 1) {
            const data = selectedElements[Object.keys(selectedElements)[0]];
            // TODO: This check will be removed once the tech debt about UNDO remove selection is fixed.
            // In reality, selectedElements.length should be zero once undo removes the element
            const doesElementExist = this.checkIfElementExists(data);
            shouldDisplayToolbar = data.type === elementTypes.PARAGRAPH && doesElementExist;
        }

        return (
            <DndProvider backend={HTML5Backend}>
                {hasCustomFontsAccess && !isCustomFontsLoaded && (
                    <LoadingMessage text="Loading custom fonts..." isLoading={!isCustomFontsLoaded} />
                )}

                {!flags.pik1913TextEditorModule && isForceUpdateFontModalOpen && (
                    <ForceUpdateFont
                        canvasBlocks={canvasBlocks}
                        missingFonts={missingFonts}
                        dispatch={dispatch}
                        closeModal={() => {
                            dispatch(modalActions.setModal({ name: 'forceUpdateFont', status: false }));
                        }}
                    />
                )}

                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <TopNav
                        // TODO: PIK-2203 Cleanup prop drilling
                        applicationMode={appMode}
                        setApplicationMode={(mode) => () => {
                            dispatch(browserActions.appModeChanged(mode));
                        }}
                        setPreviewWidth={(width) => () =>
                            this.setState((prevState) => ({
                                ...prevState,
                                previewWidth: width,
                            }))}
                    />

                    <div
                        data-test-id="preview-container"
                        style={{
                            height: '100%',
                            display: appMode === APPLICATION_MODES.PREVIEW ? null : 'none',
                        }}
                    >
                        <Preview previewWidth={previewWidth} />
                    </div>

                    {appMode === APPLICATION_MODES.VERSION_HISTORY && (
                        <div data-test-id="preview-container" style={{ height: '100%' }}>
                            <VersionHistory
                                resetVersionHistory={() =>
                                    dispatch(browserActions.appModeChanged(APPLICATION_MODES.EDITOR))
                                }
                            />
                        </div>
                    )}

                    <div
                        style={{
                            display: appMode === APPLICATION_MODES.EDITOR ? 'flex' : 'none',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            height: 'calc(100% - 50px)',
                            backgroundColor: 'white',
                        }}
                    >
                        <LeftPanel
                            shouldLeftPanelBeCollapsed={shouldLeftPanelBeCollapsed}
                            leftPanelCollapsed={leftPanelCollapsed}
                            leftPanelWidth={leftPanelWidth}
                            setUserCollapsePreference={(status) => this.setState({ leftPanelCollapsedByUser: status })}
                        />
                        <div
                            className="scrollFix"
                            id="canvasContainer"
                            data-test-id="canvas-container"
                            style={{
                                width: `calc(100% - 250px - ${leftPanelWidth}px)`,
                                height: 'calc(100% - 40px)',
                                backgroundColor: '#ededef',
                                overflowY: isCanvasEmpty ? 'hidden' : 'scroll',
                                marginTop: 35,
                                border: '1px solid #CCCCCC',
                                zIndex: flags.pik1913TextEditorModule ? 0 : 'auto',
                            }}
                        >
                            {shouldDisplayToolbar && !flags.pik1913TextEditorModule && (
                                <Styled.ToolbarDiv leftPanelCollapsed={leftPanelCollapsed}>
                                    <Toolbar leftPanelCollapsed={leftPanelCollapsed} />
                                </Styled.ToolbarDiv>
                            )}
                            <Canvas
                                canvasBlocks={canvasBlocks}
                                isCanvasEmpty={isCanvasEmpty}
                                leftPanelCollapsed={leftPanelCollapsed}
                            />
                            {displayErrorAddingBlock && (
                                <Notifier
                                    type="ERROR"
                                    message="You have reached your maximum allowed number of blocks. Request a full product demo today."
                                />
                            )}
                            {displayInternetWarning && (
                                <Notifier
                                    type="WARNING"
                                    message="The connection is too unstable to support real-time collaboration. To troubleshoot, visit our"
                                    link="https://support.staffbase.com/hc/en-us/articles/4412584430354"
                                    linkTitle="Help Center"
                                />
                            )}
                        </div>
                        <div data-test-id="right-panel-container" style={{ width: 250, height: '100%' }}>
                            <RightPanel />
                        </div>
                    </div>
                </div>
            </DndProvider>
        );
    }
}

Editor.propTypes = {
    browserWidth: PropTypes.number.isRequired,
    browserHeight: PropTypes.number.isRequired,
    canvasBlocks: PropTypes.arrayOf(blockType).isRequired,
    selectedElements: PropTypes.object.isRequired,
    isConnected: PropTypes.bool.isRequired,
    integration: PropTypes.string.isRequired,
    hasRTEAccess: PropTypes.bool.isRequired,
    hasCustomFontsAccess: PropTypes.bool.isRequired,
    isCustomFontsLoaded: PropTypes.bool.isRequired,
    customFonts: PropTypes.object.isRequired,
    isForceUpdateFontModalOpen: PropTypes.bool.isRequired,
    flags: PropTypes.shape({
        pik1913TextEditorModule: PropTypes.bool.isRequired,
        ed563MailMergeV2: PropTypes.bool.isRequired,
    }).isRequired,
    appMode: PropTypes.oneOf(Object.values(APPLICATION_MODES)).isRequired,
    dispatch: PropTypes.func.isRequired,
    isGetSingleUseTagsLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
    const hasCustomFontsEnabled = _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false);
    const isCustomFontsLoaded = _get(state, 'customFonts.isLoaded', false);
    const customFontsData = _get(state, 'customFonts.data', {});

    return {
        canvasBlocks: state.canvasBlocks.present,
        selectedElements: state.selectedElements.local,
        isConnected: _get(state, 'session.collaboration.isConnected', false),
        integration: _get(state, 'session.integration', 'LOCALHOST'),
        hasRTEAccess: _get(state, 'session.features.F_RTE', false),
        isForceUpdateFontModalOpen: _get(state, 'modals.forceUpdateFont.status', false),
        hasCustomFontsAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false),
        isCustomFontsLoaded: _get(state, 'customFonts.isLoaded', false),
        customFonts:
            hasCustomFontsEnabled && isCustomFontsLoaded && !_isEmpty(customFontsData)
                ? new CustomFonts(customFontsData)
                : [],
        appMode: _get(
            state,
            'browser.appMode',
            state.session.collaboration.access === DRAFT_ACCESS.READ
                ? APPLICATION_MODES.PREVIEW
                : APPLICATION_MODES.EDITOR
        ),
        isGetSingleUseTagsLoading: _get(state, 'personalization.tag.isGetSingleUseTagsLoading', true),
    };
};

export default withLDConsumer()(connect(mapStateToProps)(Editor));
